import cs from "classnames";
import React from "react";

import { useTrackFocusState } from "src/common/hooks/useTrackFocusState";

import "./Radio.css";

const noop = (): void => {};

interface RadioProps {
	disabled?: boolean;
	checked?: boolean;
	value?: string | undefined;
	onChangeValue?(value: string | undefined): unknown;
}

export const Radio: React.FunctionComponent<
	React.PropsWithChildren<RadioProps>
> = ({ disabled, checked, value, onChangeValue = noop, children }) => {
	const [isFocused, onFocus, onBlur] = useTrackFocusState();

	const onKeyDown = React.useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key !== " ") {
				return;
			}
			event.preventDefault();
			onChangeValue(value);
			onFocus();
		},
		[onChangeValue, onFocus, value]
	);

	const onChange = React.useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			onChangeValue(event.target.value);
		},
		[onChangeValue]
	);

	return (
		<label
			className={cs("radio", {
				"radio--disabled": disabled,
				"radio--checked": checked,
				"radio--focused": isFocused
			})}
		>
			<div
				role="radio"
				aria-checked={checked}
				className="radio__input"
				tabIndex={disabled ? -1 : 0}
				onFocus={onFocus}
				onBlur={onBlur}
				onClick={onBlur}
				onKeyDown={onKeyDown}
			>
				<div className="radio__ripple" />
				<div className="radio__circle" />
				<input
					type="radio"
					checked={checked}
					value={value}
					onChange={onChange}
				/>
			</div>
			{children && <div className="radio__label">{children}</div>}
		</label>
	);
};
